import { createSlice } from "@reduxjs/toolkit";

/**
 * Redux slice for the AFMS Dealer/Vendor Shared Asset Dashboard (portal).
 * Structure mirrors other AFMS slices — each concern is a
 * { loading, error, data } bucket driven by thunks in actions.js.
 */

const bucket = (data = null) => ({ loading: false, error: null, data });

const initialState = {
  assetList: bucket([]),
  assetDetail: bucket(),
  timeline: bucket([]),
  morningBrief: bucket(),
  routeReport: bucket(),
  // Transient in-flight state for mutations so the UI can disable buttons
  // and surface errors without stomping the list cache.
  mutation: bucket(),
};

const portalSlice = createSlice({
  name: "portal",
  initialState,
  reducers: {
    assetListStart: (state) => {
      state.assetList.loading = true;
      state.assetList.error = null;
    },
    assetListSuccess: (state, action) => {
      state.assetList.loading = false;
      state.assetList.data = action.payload;
    },
    assetListFailure: (state, action) => {
      state.assetList.loading = false;
      state.assetList.error = action.payload;
    },

    // Apply a single updated asset summary into the cached list in place
    // so the grid rerenders without a full refetch.
    assetListUpsert: (state, action) => {
      const updated = action.payload;
      if (!state.assetList.data || !updated) return;
      const idx = state.assetList.data.findIndex((a) => a.assetRef === updated.assetRef);
      if (idx >= 0) {
        state.assetList.data[idx] = updated;
      } else {
        state.assetList.data.push(updated);
      }
    },

    assetDetailStart: (state) => {
      state.assetDetail.loading = true;
      state.assetDetail.error = null;
    },
    assetDetailSuccess: (state, action) => {
      state.assetDetail.loading = false;
      state.assetDetail.data = action.payload;
    },
    assetDetailFailure: (state, action) => {
      state.assetDetail.loading = false;
      state.assetDetail.error = action.payload;
    },
    assetDetailReset: (state) => {
      state.assetDetail = bucket();
    },

    timelineStart: (state) => {
      state.timeline.loading = true;
      state.timeline.error = null;
    },
    timelineSuccess: (state, action) => {
      state.timeline.loading = false;
      state.timeline.data = action.payload;
    },
    timelineFailure: (state, action) => {
      state.timeline.loading = false;
      state.timeline.error = action.payload;
    },

    morningBriefStart: (state) => {
      state.morningBrief.loading = true;
      state.morningBrief.error = null;
    },
    morningBriefSuccess: (state, action) => {
      state.morningBrief.loading = false;
      state.morningBrief.data = action.payload;
    },
    morningBriefFailure: (state, action) => {
      state.morningBrief.loading = false;
      state.morningBrief.error = action.payload;
    },

    routeReportStart: (state) => {
      state.routeReport.loading = true;
      state.routeReport.error = null;
    },
    routeReportSuccess: (state, action) => {
      state.routeReport.loading = false;
      state.routeReport.data = action.payload;
    },
    routeReportFailure: (state, action) => {
      state.routeReport.loading = false;
      state.routeReport.error = action.payload;
    },

    mutationStart: (state) => {
      state.mutation.loading = true;
      state.mutation.error = null;
    },
    mutationSuccess: (state, action) => {
      state.mutation.loading = false;
      state.mutation.data = action.payload;
    },
    mutationFailure: (state, action) => {
      state.mutation.loading = false;
      state.mutation.error = action.payload;
    },
    mutationReset: (state) => {
      state.mutation = bucket();
    },
  },
});

export const {
  assetListStart,
  assetListSuccess,
  assetListFailure,
  assetListUpsert,
  assetDetailStart,
  assetDetailSuccess,
  assetDetailFailure,
  assetDetailReset,
  timelineStart,
  timelineSuccess,
  timelineFailure,
  morningBriefStart,
  morningBriefSuccess,
  morningBriefFailure,
  routeReportStart,
  routeReportSuccess,
  routeReportFailure,
  mutationStart,
  mutationSuccess,
  mutationFailure,
  mutationReset,
} = portalSlice.actions;

export default portalSlice.reducer;
